import type { EntityRef, OwnerRef, ProvenanceBadge, Trend } from '@aurora-pvi/contracts';
import type { Application, AuroraDataset, Product, Provenance } from '@aurora-pvi/seed';
import { ROUTES, buildPath } from '@aurora-pvi/contracts';
import { round } from '@aurora-pvi/seed';
import type { ScopeIndex } from './filters';
import type { SeededPersona } from './types';

/** Small projection helpers shared by every repository. */

export function ownerRef(
  index: ScopeIndex,
  user: SeededPersona,
  personCode: string | null | undefined,
): OwnerRef | null {
  if (!personCode) return null;
  const person = index.peopleByCode.get(personCode);
  if (!person) return null;
  return {
    id: person.id,
    displayName: person.displayName,
    jobTitle: person.jobTitle,
    // Contact detail is workforce data, so it is masked rather than invented.
    email: user.canSeeWorkforce ? person.email : null,
  };
}

export function productRef(product: Product): EntityRef {
  return {
    id: product.id,
    entityType: 'Product',
    businessCode: product.businessCode,
    name: product.name,
    route: buildPath(ROUTES.product360, { productId: product.id }),
  };
}

export function applicationRef(application: Application): EntityRef {
  return {
    id: application.id,
    entityType: 'Application',
    businessCode: application.businessCode,
    name: application.name,
    route: buildPath(ROUTES.applicationDetail, { applicationId: application.id }),
  };
}

export function simpleRef(
  id: string,
  entityType: string,
  businessCode: string | null,
  name: string,
  route: string | null = null,
): EntityRef {
  return { id, entityType, businessCode, name, route };
}

export function provenanceBadge(provenance: Provenance): ProvenanceBadge {
  return {
    sourceSystem: provenance.sourceSystem,
    refreshedAt: provenance.sourceUpdatedAt,
    confidence: provenance.confidenceBand,
    qualityScore: round(provenance.confidence * 100, 0),
  };
}

/** Build a trend from an ordered series, newest last. */
export function trendFrom(points: readonly number[]): Trend {
  const cleaned = points.filter((p) => Number.isFinite(p));
  if (cleaned.length < 2) {
    return { points: cleaned.slice(), direction: 'flat', changePercent: null };
  }
  const first = cleaned[0] ?? 0;
  const last = cleaned[cleaned.length - 1] ?? 0;
  const changePercent = first === 0 ? null : round((last - first) / first, 4);
  return {
    points: cleaned.slice(),
    direction: last > first ? 'up' : last < first ? 'down' : 'flat',
    changePercent,
  };
}

export function valueStreamRef(dataset: AuroraDataset, code: string): EntityRef | null {
  const stream = dataset.valueStreams.find((v) => v.businessCode === code);
  if (!stream) return null;
  return simpleRef(stream.id, 'ValueStream', stream.businessCode, stream.name, null);
}

export function productGroupRef(dataset: AuroraDataset, code: string): EntityRef | null {
  const group = dataset.productGroups.find((g) => g.businessCode === code);
  if (!group) return null;
  return simpleRef(group.id, 'ProductGroup', group.businessCode, group.name, null);
}

export function departmentRef(dataset: AuroraDataset, code: string): EntityRef | null {
  const department = dataset.departments.find((d) => d.businessCode === code);
  if (!department) return null;
  return simpleRef(
    department.id,
    'OrganizationalUnit',
    department.businessCode,
    department.name,
    null,
  );
}

export const RISK_DRIVER_LABELS: Record<string, string> = {
  reliability: 'Reliability',
  cost: 'Cost',
  delivery: 'Delivery',
  techdebt: 'Technical debt',
  compliance: 'Compliance',
  dataquality: 'Data quality',
  supplier: 'Supplier',
};
